import {
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { ControlPosition } from '~/pages/LiveTracking/constants';
import { LIVE_MAP_ID } from '~/config';
import useAddStoreMarker from './useAddStoreMarker';

type DrawnZonesMapProps = {
  allLatLngs?: Array<Array<[number, number]>>;
  activeZone?: Array<[number, number]>;
  defaultCenter?: { lat: number; lng: number };
  storeLocations?: { lng?: number; lat?: number }[];
};

const DEFAULT_LOCATION = { lat: 49.2577143, lng: -122.9539433 };

const polygonOptions: google.maps.PolygonOptions = {
  clickable: false,
  editable: false,
  draggable: false,
  strokeWeight: 1,
};

const mapOptions: google.maps.MapOptions = {
  mapId: LIVE_MAP_ID,
  mapTypeControl: false,
  fullscreenControl: true,
  streetViewControl: false,
  disableDefaultUI: true,
  zoomControl: true,
  zoomControlOptions: {
    position:
      ControlPosition.RIGHT_BOTTOM as unknown as google.maps.ControlPosition,
  },
  zoom: 11,
  center: {
    lat: 49.21556039735341,
    lng: -123.09840777108371,
  },
};

export const AllZoneMap = forwardRef(
  (
    {
      allLatLngs = [],
      activeZone,
      defaultCenter,
      storeLocations,
    }: DrawnZonesMapProps,
    ref: React.Ref<any>,
  ) => {
    const mapRef = useRef<HTMLDivElement | null>(null);
    const map = useRef<google.maps.Map | null>(null);
    const [mapState, setMapState] = useState<google.maps.Map | null>(null);
    const polygonsRef = useRef<google.maps.Polygon[]>([]);
    const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(
      null,
    );
    const shapeRef = useRef<google.maps.Polygon | null>(null);

    useAddStoreMarker({ storeLocations, map: mapState });

    const initializeMap = () => {
      if (mapRef.current && !map.current) {
        map.current = new window.google.maps.Map(mapRef.current, mapOptions);
        setMapState(map.current);
      }
    };

    const drawPolygon = (
      paths: Array<{ lat: number; lng: number }>,
      isActive = false,
    ) => {
      if (!map.current) return;
      const polygon = new google.maps.Polygon({
        paths,
        ...polygonOptions,
        fillOpacity: isActive ? 0.6 : 0.2,
        ...(isActive && {
          strokeColor: '#1565C0',
          fillColor: '#1565C0',
          zIndex: 99,
        }),
      });
      polygon.setMap(map.current);
      polygonsRef.current.push(polygon);
    };

    const isSamePolygon = (
      a: Array<[number, number]>,
      b: Array<[number, number]>,
    ) =>
      a.length === b.length &&
      a.every(([lngA, latA], i) => {
        const [lngB, latB] = b[i] || [];
        return lngA === lngB && latA === latB;
      });

    const fitBounds = (latlngs: Array<[number, number]>) => {
      if (!map.current || !latlngs.length) return;
      const bounds = new google.maps.LatLngBounds();
      latlngs.forEach(([lng, lat]) => bounds.extend({ lng, lat }));
      map.current.fitBounds(bounds);
    };

    const allPaths = useMemo(
      () =>
        allLatLngs.map((polygon) =>
          polygon.map(([lng, lat]) => ({ lat, lng })),
        ),
      [allLatLngs],
    );

    const activePaths = useMemo(
      () => activeZone?.map(([lng, lat]) => ({ lat, lng })) ?? [],
      [activeZone],
    );

    useEffect(() => {
      initializeMap();
    }, []);

    useEffect(() => {
      if (!map.current || drawingManagerRef.current) return;
      const manager = new google.maps.drawing.DrawingManager({
        drawingControl: false,
        polygonOptions,
      });
      manager.setMap(map.current);
      drawingManagerRef.current = manager;
    }, []);

    useEffect(() => {
      if (!map.current) return;

      polygonsRef.current.forEach((p) => p.setMap(null));
      polygonsRef.current = [];

      allLatLngs.forEach((polygon, i) => {
        const isActive = activeZone && isSamePolygon(polygon, activeZone);
        if (!isActive) drawPolygon(allPaths[i], false);
      });

      if (activeZone) {
        drawPolygon(activePaths, true);
        fitBounds(activeZone);
      } else if (allLatLngs.length) {
        fitBounds(allLatLngs.flat());
      } else {
        map.current.setCenter(defaultCenter || DEFAULT_LOCATION);
      }
    }, [allLatLngs, activeZone]);

    useImperativeHandle(ref, () => ({
      getPaths: (): [number, number][] => {
        if (!shapeRef.current) return [];
        const path = shapeRef.current.getPath();
        const result: [number, number][] = [];
        for (let i = 0; i < path.getLength(); i += 1) {
          const point = path.getAt(i);
          result.push([point.lng(), point.lat()]);
        }
        return result;
      },
    }));

    return (
      <div
        ref={mapRef}
        id='drawn-zones-map'
        style={{
          marginTop: 8,
          minHeight: 280,
          width: '100%',
          position: 'relative',
          borderRadius: 8,
        }}
      />
    );
  },
);
